"use client"

import { Button } from '../../../../../shared/components/ui/button'
import { 
    yesNoRejectedOptions 
} from '../../../../../shared/constants/options'
import { updateHomeOwner } from '@/features/homeowners/api/homeowners.api'
import { updateHome } from '@/features/homeowners/api/homes.api'
import { 
    residentTypeOptions, 
    workScopeStatusOptions 
} from '@/features/homeowners/constants/options'
import { 
    homeOwnerFormSchema, 
    HomeOwnerFormType, 
    UpdateHomeOwnerFormType 
} from '@/features/homeowners/schema/home-owner.schema'
import { z } from 'zod'
import { homeWithHomeOwnerFormSchema, HomeWithHomeOwnerFormType } from '@/features/homeowners/schema/home-with-home-owner.schema'
import { HomeOwner } from '@/features/homeowners/types/backend-response.type'
import errorToast from '../../../../../shared/lib/toast/error.toast'
import successToast from '../../../../../shared/lib/toast/success.toast'
import { zodResolver } from '@hookform/resolvers/zod'
import { useParams } from 'next/navigation'
import React from 'react'
import { DefaultValues, FormProvider, useForm } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import { HOME_OWNERS } from '../../../../../shared/constants/backend-api'
import { INVALID_PHONE_ERROR } from '@/features/auth/constants/backend-errors'
import { useEffect, useMemo, useState, useRef } from 'react'
import { DirtyFieldsProvider } from '../../../context/dirty-fields-context'
import StickySaveBar from '../../sticky-save-bar'
import { isNoEmailPlaceholderValue } from '@/features/volunteers/schemas/volunteers.schema'

type UpdateHomeOwnerFormProviderProps = {
    children: React.ReactNode,
    homeOwner: HomeOwner,
}


function UpdateHomeOwnerFormProvider({ children, homeOwner }: UpdateHomeOwnerFormProviderProps) {
    const canEditEmailOnUpdate = isNoEmailPlaceholderValue(homeOwner?.user?.email)

    // State for sticky save bar functionality
    const [isFormClean, setIsFormClean] = useState(false)
    const [showSuccessBar, setShowSuccessBar] = useState(false)
    const hasInitialized = useRef(false)
    
    // Helper function to ensure phone numbers are in E.164 format for consistent display
    const formatPhoneForForm = (phone: string | undefined | null): string | undefined => {
        if (!phone) return undefined;
        // If phone already has + prefix, return as-is (E.164 format)
        if (phone.startsWith('+')) {
            return phone;
        }
        // If phone is 10 digits, add +1 prefix to make it E.164
        const cleanDigits = phone.replace(/\D/g, '');
        if (cleanDigits.length === 10) {
            return `+1${cleanDigits}`;
        }
        // If phone is 11 digits starting with 1, add + prefix
        if (cleanDigits.length === 11 && cleanDigits.startsWith('1')) {
            return `+${cleanDigits}`;
        }
        // Return as-is for other formats (let validation handle it)
        return phone;
    };

    
    const defaultValues: DefaultValues<HomeWithHomeOwnerFormType> = useMemo(() => ({
        homeOwner: {
            name: homeOwner.user.fullname,
            phone: formatPhoneForForm(homeOwner.user.phone),
            primaryLanguage: homeOwner.primaryLanguage,
            email: homeOwner.user.email,
            secondaryContact: homeOwner.secondaryContact,
            secondaryPhone: formatPhoneForForm(homeOwner.secondaryPhone),
            dateOfBirth: (() => {
                // dateOfBirth is stored in the user table, not the homeowner table
                const dateValue = homeOwner.user?.dateOfBirth;
                
                if (!dateValue) {
                    return undefined;
                }
                
                // If it's already a Date object, convert to local date (avoiding timezone issues)
                if (dateValue instanceof Date) {
                    // Extract local date components to create a date-only Date object (no time component)
                    return new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate());
                }
                
                // If it's a string, parse as local date (YYYY-MM-DD format)
                if (typeof dateValue === 'string') {
                    const dateStr = dateValue.split('T')[0]; // Get just the date part if ISO string
                    const [year, month, day] = dateStr.split('-').map(Number);
                    
                    // Validate parsed values
                    if (!isNaN(year) && !isNaN(month) && !isNaN(day) && year > 0 && month > 0 && month <= 12 && day > 0 && day <= 31) {
                        // Create local date (not UTC) to avoid timezone shifts
                        return new Date(year, month - 1, day);
                    }
                }
                
                return undefined;
            })(),
            residentType: homeOwner.residentType as typeof residentTypeOptions[number]["value"],
            medicalNeeds: homeOwner.medicalNeeds,
            numChildrenUnder18: homeOwner.numChildrenUnder18,
            isFirstResponder: homeOwner.isFirstResponder === "yes" ? "yes" : homeOwner.isFirstResponder === "no" ? "no" : undefined,
            isSingleParent: homeOwner.isSingleParent === "yes" ? "yes" : homeOwner.isSingleParent === "no" ? "no" : undefined,
            isVeteran: homeOwner.isVeteran === "yes" ? "yes" : homeOwner.isVeteran === "no" ? "no" : undefined,
            appliedFEMA: homeOwner.appliedFEMA === "yes" ? "yes" : homeOwner.appliedFEMA === "no" ? "no" : undefined,
            femaStatus: homeOwner.femaStatus as typeof yesNoRejectedOptions[number]["value"],
            appliedSBA: homeOwner.appliedSBA === "yes" ? "yes" : homeOwner.appliedSBA === "no" ? "no" : undefined,
            sbaStatus: homeOwner.sbaStatus as typeof yesNoRejectedOptions[number]["value"],
            safetyConcerns: homeOwner.safetyConcerns,
            notesAndComments: homeOwner.prayerRequest,
            ownerStory: homeOwner.ownerStory,
        },
        home: {
            // Fields used by StateCountryAutocomplete component
            address: homeOwner.home?.address || "",
            city: homeOwner.home?.city || "",
            state: homeOwner.home?.state || "",
            zipCode: homeOwner.home?.zipCode || "",
            latitude: homeOwner.home?.latitude ? Number(homeOwner.home.latitude) : undefined,
            longitude: homeOwner.home?.longitude ? Number(homeOwner.home.longitude) : undefined,
            // Fields used by form inputs
            county: homeOwner.home?.county || "",
            hasFloodInsurance: homeOwner.home?.hasFloodInsurance,
            hasHomeInsurance: homeOwner.home?.hasHomeInsurance,
        }
    }), [homeOwner])

    // Debug logging

    const params = useParams()
    const homeOwnerId = parseInt(params["homeowner-id"] as string)

    const queryClient = useQueryClient()

    
    // Create a simplified schema for home fields only (like the working form)
    // Exclude googlePicLink (deprecated, photos now in S3)
    const simplifiedHomeSchema = z.object({
        // Fields used by StateCountryAutocomplete component
        address: z.string().optional(),
        city: z.string().optional(),
        state: z.string().optional(),
        zipCode:  z.string().nullable().optional().or(z.literal('')),
        latitude: z.number().optional(),
        longitude: z.number().optional(),
        // Fields used by form inputs
        county: z.string().optional(),
        hasFloodInsurance: z.string().nullable().optional(),
        hasHomeInsurance: z.string().nullable().optional(),
        // Explicitly exclude googlePicLink - not in schema
    });

    const form = useForm<HomeWithHomeOwnerFormType>({
        resolver: zodResolver(z.object({ 
            homeOwner: homeOwnerFormSchema.optional(),
            home: simplifiedHomeSchema 
        })),
        defaultValues,
        mode: "onChange",
        shouldFocusError: false,
    })
    
    const {  handleSubmit, formState: { errors, dirtyFields, isDirty, isSubmitting }, getValues, setValue, watch, reset } = form

    // Reset form after initialization to clear any initial dirty state
    // This ensures the form starts clean even if defaultValues transformations cause initial differences
    useEffect(() => {
        if (!hasInitialized.current) {
            reset(defaultValues, { keepDefaultValues: true });
            hasInitialized.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    // Clear clean state when form becomes dirty
    useEffect(() => {
        if (isDirty && isFormClean) {
            setIsFormClean(false);
        }
    }, [isDirty, isFormClean]);
    
    // Also clear clean state when user makes any changes (even if form isn't dirty yet)
    useEffect(() => {
        if (isFormClean) {
            // Add a small delay to prevent immediate clearing after form reset
            const timeoutId = setTimeout(() => {
                // Watch for any form changes to clear the clean state
                const subscription = watch(() => {
                    if (isFormClean) {
                        setIsFormClean(false);
                    }
                });
                return () => subscription.unsubscribe();
            }, 100); // 100ms delay
            
            return () => clearTimeout(timeoutId);
        }
    }, [isFormClean, watch]);


    const onSubmit = async (values: HomeWithHomeOwnerFormType) => {
        
        try {
            const normalizedEmail = String(values.homeOwner?.email ?? '').trim().toLowerCase()
            const hasTypedEmailForPlaceholder = canEditEmailOnUpdate && normalizedEmail.length > 0 && !isNoEmailPlaceholderValue(normalizedEmail)

            // Handle home data updates (for flood insurance and homeowner's insurance)
            // Exclude googlePicLink (deprecated, photos now in S3)
            const updateHomeData = Object.keys(dirtyFields.home || {}).reduce((acc, key) => {
                // Skip googlePicLink - it's deprecated and should not be updated
                if (key === 'googlePicLink') {
                    return acc;
                }
                // Skip materialsNeeded - it's not mapped to a form field anymore and not in UpdateHomeDto
                // Including it causes "Invalid relation key: materialsNeeded" errors in the backend
                if (key === 'materialsNeeded') {
                    return acc;
                }
                // Type-safe access: ensure key exists on values.home
                if (key in values.home) {
                    acc[key] = (values.home as any)[key];
                }
                return acc;
            }, {} as Record<string, any>);

            // Handle homeowner data updates
            const updateHomeOwnerData = Object.keys(dirtyFields.homeOwner || {}).reduce((acc, key) => {
                // Type-safe access: ensure key exists on values.homeOwner
                if (!(key in values.homeOwner)) {
                    return acc;
                }
                const value = (values.homeOwner as any)[key]
                
                // Convert dateOfBirth to YYYY-MM-DD string format for backend
                if (key === 'dateOfBirth') {
                    if (value instanceof Date) {
                        // IMPORTANT: Extract date components using local timezone methods
                        // to ensure we get the date the user actually selected, not a UTC-converted date
                        // The Date object from the calendar picker is created at midnight local time,
                        // so we need to use local date methods (getFullYear, getMonth, getDate)
                        // not UTC methods (getUTCFullYear, etc.)
                        const year = value.getFullYear();
                        const month = value.getMonth() + 1; // getMonth() returns 0-11
                        const day = value.getDate();
                        
                        // Format as YYYY-MM-DD string
                        const dateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        acc[key] = dateString;
                    } else if (value) {
                        // If it's already a string, validate and use it
                        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
                            // Extract just the date part (YYYY-MM-DD) if it's an ISO string
                            acc[key] = value.split('T')[0];
                        } else {
                            acc[key] = value;
                        }
                    } else {
                        acc[key] = null;
                    }
                } else if (key === 'notesAndComments') {
                    // Map notesAndComments to prayerRequest for backend
                    acc['prayerRequest'] = value
                } else if (key === 'email') {
                    if (!canEditEmailOnUpdate) {
                        return acc
                    }
                    const trimmedEmail = String(value ?? '').trim().toLowerCase()
                    if (trimmedEmail && !isNoEmailPlaceholderValue(trimmedEmail)) {
                        acc[key] = trimmedEmail
                    }
                } else if (key === 'phone' || key === 'secondaryPhone') {
                    // Transform phone numbers to E.164 format for backend
                    if (value && typeof value === 'string' && value.trim() !== '') {
                        // Use the same transformation logic as the working form
                        if (value.startsWith('+')) {
                            acc[key] = value; // Already in E.164 format
                        } else {
                            const cleanDigits = value.replace(/\D/g, '');
                            if (cleanDigits.length === 10) {
                                acc[key] = `+1${cleanDigits}`;
                            } else if (cleanDigits.length === 11 && cleanDigits.startsWith('1')) {
                                acc[key] = `+${cleanDigits}`;
                            } else {
                                acc[key] = value; // Keep as-is if not a standard format
                            }
                        }
                    } else {
                        // For empty/undefined values, send null instead of empty string
                        // This prevents backend validation errors for optional phone fields
                        acc[key] = null;
                    }
                } else {
                    // For all other fields, pass through as-is
                    // For optional string fields that are undefined, send null to match backend expectations
                    if (value === undefined || value === '') {
                        acc[key] = null;
                    } else {
                        acc[key] = value;
                    }
                }
                
                return acc;
            }, {} as Record<string, any>);

            if (!canEditEmailOnUpdate) {
                delete updateHomeOwnerData.email
            } else if (hasTypedEmailForPlaceholder) {
                updateHomeOwnerData.email = normalizedEmail
            }

            let hasErrors = false;

            // Update home data if there are changes
            if (Object.keys(updateHomeData).length > 0) {
                const homeId = homeOwner.home?.id;
                if (homeId) {
                    const updateHomeRes = await updateHome(homeId, updateHomeData);
                    
                    if (!updateHomeRes.success) {
                        errorToast(`Failed to update home details: ${updateHomeRes.message}`);
                        hasErrors = true;
                    }
                }
            }

            // Update homeowner data if there are changes
            if (Object.keys(updateHomeOwnerData).length > 0) {
                const updateHomeOwnerRes = await updateHomeOwner(homeOwnerId, updateHomeOwnerData as UpdateHomeOwnerFormType)
                
                if(!updateHomeOwnerRes.success) {
                    const errorResponse = updateHomeOwnerRes as { success: false; status: number; message: string };
                    if(errorResponse.message === INVALID_PHONE_ERROR) {
                        errorToast("Invalid phone number")
                        return
                    }
                    errorToast(errorResponse.message)
                    return
                }
            }

            if (!hasErrors) {
                successToast("Details updated successfully")
                
                // Refetch Homeowners list so recently modified home appears at top
                await queryClient.invalidateQueries({ queryKey: ['homes', 'filter-search'] })
                await queryClient.invalidateQueries({ queryKey: ['homes', 'lightweight'] })
                queryClient.invalidateQueries({ queryKey: [HOME_OWNERS.base] })
                
                // Mark form as clean by resetting with current values
                const currentValues = getValues();
                
                // Reset the form with current values to clear dirty state
                form.reset(currentValues);
                
                // Show visual feedback that form was saved
                setIsFormClean(true) // Mark form as clean to disable button
                setShowSuccessBar(true) // Show the sticky success bar
            }
        } catch (error) {
            errorToast("An error occurred while updating details")
        }
    }

    // Hide success bar when form becomes dirty again
    useEffect(() => {
        if (isDirty && showSuccessBar) {
            setShowSuccessBar(false)
        }
    }, [isDirty, showSuccessBar])

    // Handler for sticky bar save button
    const handleStickySave = async () => {
        // Just get the current form data and submit it
        // The onSubmit function already handles validation and dirty field checking
        const data = form.getValues();
        await onSubmit(data);
    }
    
    
    return (
        <FormProvider {...form}>
            <DirtyFieldsProvider isFormClean={isFormClean}>
                <form onSubmit={form.handleSubmit(onSubmit)} className='w-full flex flex-col gap-5 pb-12'>
                    {children}
                </form>
                <StickySaveBar 
                    isVisible={isDirty || showSuccessBar}
                    isSuccess={showSuccessBar}
                    isSubmitting={isSubmitting}
                    onSave={handleStickySave}
                />
            </DirtyFieldsProvider>
        </FormProvider>
    )
}

export default UpdateHomeOwnerFormProvider
